import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, CheckCircle2, AlertCircle, Package, ArrowRightLeft, Clock } from 'lucide-react';

export default function ExitClearancePage() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assets/exit-clearance/${user_id}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  }, [user_id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-[1000px] mx-auto px-4 py-10 text-sm text-ink-2">Loading…</div>;
  if (!data) return <div className="max-w-[1000px] mx-auto px-4 py-10 text-sm text-ink-2">Not found.</div>;

  const complete = data.status === 'complete';
  const blocking = data.blocking || [];
  const pin = data.pending_in || [];
  const pout = data.pending_out || [];
  const pret = data.pending_return || [];

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="exit-clearance-page">
      <Link to="/assets" className="text-xs text-ink-2 inline-flex items-center gap-1 hover:text-ink">
        <ChevronLeft className="h-3 w-3" /> Back to Assets
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Exit clearance — {data.user?.name}</h1>
          <div className="text-xs text-ink-2 mt-1">{data.user?.role} · {data.user?.email}</div>
        </div>
        {complete ? (
          <Badge className="bg-emerald-100 text-emerald-700 text-sm px-3 py-1" data-testid="clearance-badge-complete">
            <CheckCircle2 className="h-4 w-4 mr-1 inline" /> Clearance complete
          </Badge>
        ) : (
          <Badge className="bg-rose-100 text-rose-700 text-sm px-3 py-1" data-testid="clearance-badge-pending">
            <AlertCircle className="h-4 w-4 mr-1 inline" /> Clearance pending ({data.count_total} item{data.count_total === 1 ? '' : 's'})
          </Badge>
        )}
      </div>

      {!complete && (
        <Card className="shine-card p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="clearance-warning">
          <div className="text-xs text-amber-900">
            <b>Don't deactivate this user yet.</b> They still have {data.count_total} asset{data.count_total === 1 ? '' : 's'} on file. Each must be
            {' '}<b>Returned</b> (keeper confirms) or <b>Transferred</b> (receiver confirms) before clearance can be marked complete.
          </div>
        </Card>
      )}

      <Section title="Still holding — must return or transfer" items={blocking} tone="rose" icon={Package} />
      <Section title="Pending return — keeper must confirm" items={pret} tone="amber" icon={Clock} />
      <Section title="Pending outgoing transfer — receiver must confirm" items={pout} tone="orange" icon={ArrowRightLeft} />
      <Section title="Incoming transfer — this user must confirm/reject" items={pin} tone="orange" icon={ArrowRightLeft} />
    </div>
  );
}

function Section({ title, items, tone, icon: Icon }) {
  if (!items.length) return null;
  const toneCls = { rose: 'text-rose-700', amber: 'text-amber-700', orange: 'text-orange-700' }[tone] || 'text-ink';
  return (
    <Card className="shine-card p-4 rounded-xl" data-testid={`section-${tone}`}>
      <div className={`flex items-center gap-2 text-sm font-semibold mb-3 ${toneCls}`}>
        <Icon className="h-4 w-4" /> {title} <span className="text-ink-2 font-normal">· {items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((a) => (
          <Link key={a.id} to={`/assets/${a.asset_id}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-ink/10 bg-white hover:bg-page">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink"><span className="font-medium">{a.name}</span> <span className="text-ink-2 font-mono text-xs">({a.asset_id})</span></div>
              <div className="text-[11px] text-ink-2">condition {a.condition || '—'}{a.expected_return && ` · due ${a.expected_return}`}</div>
            </div>
            <Badge className="bg-slate-100 text-slate-700">Open</Badge>
          </Link>
        ))}
      </div>
    </Card>
  );
}
